/**
 * Part 11 — Templates, Admin Tooling, Power-User Ergonomics.
 *
 * Wraps three endpoint groups:
 *   - A11.1 /org-templates     — no-code admin template editor
 *   - A11.3 /saved-views       — per-user / per-org list configs
 *   - A11.2 /bulk/tasks*        — bulk operations on task lists
 */
import { apiClient } from './api'

// ── A11.1 Org templates ─────────────────────────────────────────────────────
export type TemplateKind = 'project' | 'task' | 'status' | 'evidence' | 'custom_field'

export interface OrgTemplate {
  id: number
  organization_id: number | null
  created_by_id: number | null
  kind: TemplateKind
  name: string
  description: string | null
  payload: Record<string, unknown>
  is_active: boolean
}

export interface OrgTemplateInput {
  kind: TemplateKind
  name: string
  description?: string | null
  payload?: Record<string, unknown>
  is_active?: boolean
}

// ── A11.3 Saved views ───────────────────────────────────────────────────────
export type ViewEntity = 'task' | 'project' | 'portfolio' | 'program' | 'risk' | 'lesson'

export interface SavedView {
  id: number
  organization_id: number | null
  owner_id: number
  entity_type: ViewEntity
  name: string
  config: Record<string, unknown>
  is_shared: boolean
  is_default: boolean
}

export interface SavedViewInput {
  entity_type: ViewEntity
  name: string
  config?: Record<string, unknown>
  is_shared?: boolean
  is_default?: boolean
}

// ── A11.2 Bulk ops ──────────────────────────────────────────────────────────
export interface BulkTaskAction {
  task_ids: number[]
  action: 'assign' | 'status' | 'tag' | 'untag'
  assignee_id?: number | null
  status?: string
  tag_ids?: number[]
}

export interface BulkResult {
  action: string
  requested: number
  affected: number
  skipped: number
  detail: string
}

const TPL = '/api/v1/org-templates'
const VIEWS = '/api/v1/saved-views'
const BULK = '/api/v1/bulk'

export const part11Service = {
  // A11.1
  listTemplateKinds: () => apiClient.get<string[]>(`${TPL}/kinds`).then(r => r.data),
  listTemplates: (kind?: TemplateKind, includeInactive = false) =>
    apiClient
      .get<OrgTemplate[]>(TPL, { params: { kind, include_inactive: includeInactive } })
      .then(r => r.data),
  getTemplate: (id: number) => apiClient.get<OrgTemplate>(`${TPL}/${id}`).then(r => r.data),
  createTemplate: (body: OrgTemplateInput) =>
    apiClient.post<OrgTemplate>(TPL, body).then(r => r.data),
  updateTemplate: (id: number, body: Partial<OrgTemplateInput>) =>
    apiClient.put<OrgTemplate>(`${TPL}/${id}`, body).then(r => r.data),
  deleteTemplate: (id: number) => apiClient.delete(`${TPL}/${id}`).then(() => undefined),

  // A11.3
  listViews: (entity_type?: ViewEntity) =>
    apiClient.get<SavedView[]>(VIEWS, { params: { entity_type } }).then(r => r.data),
  createView: (body: SavedViewInput) =>
    apiClient.post<SavedView>(VIEWS, body).then(r => r.data),
  updateView: (id: number, body: Partial<SavedViewInput>) =>
    apiClient.put<SavedView>(`${VIEWS}/${id}`, body).then(r => r.data),
  deleteView: (id: number) => apiClient.delete(`${VIEWS}/${id}`).then(() => undefined),

  // A11.2
  bulkTasks: (body: BulkTaskAction) =>
    apiClient.post<BulkResult>(`${BULK}/tasks`, body).then(r => r.data),
  bulkDeleteTasks: (task_ids: number[]) =>
    apiClient.post<BulkResult>(`${BULK}/tasks/delete`, { task_ids }).then(r => r.data),
  exportTasks: (task_ids: number[]) =>
    apiClient
      .post(`${BULK}/tasks/export`, { task_ids }, { responseType: 'blob' })
      .then(r => r.data as Blob),
}
